/**
 * Audio Unlock Utility
 * 
 * Modern browsers (Chrome, Safari, Firefox) block autoplay until a user gesture.
 * This utility creates a "silent" audio context on first user interaction,
 * which then allows subsequent programmatic audio playback.
 * 
 * Safari-specific notes:
 * - Safari is stricter than Chrome about autoplay
 * - The unlock MUST happen synchronously in the click handler
 * - WebKit requires multiple audio elements to be "primed"
 * - iOS Safari requires playsinline attribute on videos
 * 
 * Usage:
 * 1. Call unlockAudio() on user click (e.g., "Play All" button)
 * 2. Check isAudioUnlocked() before attempting autoplay
 * 3. The unlock persists for the page session
 */

let audioContext = null;
let isUnlocked = false;
let unlockAttempts = 0;

// Detect Safari browser
const isSafari = () => {
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent) ||
    (navigator.userAgent.includes('Safari') && !navigator.userAgent.includes('Chrome'));
};

// Create and resume AudioContext - must be called from user gesture
export const unlockAudio = async () => {
  unlockAttempts++;
  console.log(`🔓 Audio unlock attempt #${unlockAttempts} (Safari: ${isSafari()})`);
  
  try {
    // Create AudioContext if it doesn't exist
    if (!audioContext) {
      audioContext = new (window.AudioContext || window.webkitAudioContext)();
      console.log('🔓 AudioContext created, state:', audioContext.state);
    }
    
    // Resume if suspended (Safari often suspends by default)
    if (audioContext.state === 'suspended') {
      await audioContext.resume();
      console.log('🔓 AudioContext resumed, new state:', audioContext.state);
    }
    
    // Play a silent buffer to fully unlock the AudioContext
    const buffer = audioContext.createBuffer(1, 1, 22050);
    const source = audioContext.createBufferSource();
    source.buffer = buffer;
    source.connect(audioContext.destination);
    source.start(0);
    
    // ============================================================
    // SAFARI-SPECIFIC FIXES
    // Safari requires multiple audio elements to be "primed"
    // ============================================================
    
    // 1. Create and play a silent HTML5 audio element
    // This is CRITICAL for iOS Safari
    const silentAudio = new Audio('data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA');
    silentAudio.volume = 0.001; // Near-silent but not muted (Safari ignores muted)
    silentAudio.playsInline = true;
    
    try {
      const playPromise = silentAudio.play();
      if (playPromise !== undefined) {
        await playPromise;
        console.log('🔓 Silent audio played successfully');
      }
      silentAudio.pause();
    } catch (e) {
      console.log('🔓 Silent audio play attempt:', e.name);
    }
    
    // 2. Create and play an MP3-format silent audio (broader compatibility)
    const silentMp3 = new Audio('data:audio/mpeg;base64,/+NIxAAAAAANIAAAAAExBTUUzLjEwMFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV');
    silentMp3.volume = 0.001;
    silentMp3.playsInline = true;
    
    try {
      const mp3Promise = silentMp3.play();
      if (mp3Promise !== undefined) {
        await mp3Promise;
        console.log('🔓 Silent MP3 played successfully');
      }
      silentMp3.pause();
    } catch (e) {
      console.log('🔓 Silent MP3 play attempt:', e.name);
    }
    
    // 3. Safari-specific: Create a video element primer (for YouTube embeds)
    if (isSafari()) {
      try {
        const silentVideo = document.createElement('video');
        silentVideo.setAttribute('playsinline', '');
        silentVideo.setAttribute('webkit-playsinline', '');
        silentVideo.muted = true;
        silentVideo.volume = 0;
        silentVideo.src = 'data:video/mp4;base64,AAAAIGZ0eXBpc29tAAACAGlzb21pc28yYXZjMW1wNDEAAAAIZnJlZQAAA5JtZGF0AAACoAYF//+c3EXpvebZSLeWLNgg2SPu73gyNjQgLSBjb3JlIDE1MiByMjg1NCBlOWE1OTAzIC0gSC4yNjQvTVBFRy00IEFWQyBjb2RlYyAtIENvcHlsZWZ0IDIwMDMtMjAxNyAtIGh0dHA6Ly93d3cudmlkZW9sYW4ub3JnL3gyNjQuaHRtbCAtIG9wdGlvbnM6IGNhYmFjPTEgcmVmPTMgZGVibG9jaz0xOjA6MCBhbmFseXNlPTB4MzoweDExMyBtZT1oZXggc3VibWU9NyBwc3k9MSBwc3lfcmQ9MS4wMDowLjAwIG1peGVkX3JlZj0xIG1lX3JhbmdlPTE2IGNocm9tYV9tZT0xIHRyZWxsaXM9MSA4eDhkY3Q9MSBjcW09MCBkZWFkem9uZT0yMSwxMSBmYXN0X3Bza2lwPTEgY2hyb21hX3FwX29mZnNldD0tMiB0aHJlYWRzPTEgbG9va2FoZWFkX3RocmVhZHM9MSBzbGljZWRfdGhyZWFkcz0wIG5yPTAgZGVjaW1hdGU9MSBpbnRlcmxhY2VkPTAgYmx1cmF5X2NvbXBhdD0wIGNvbnN0cmFpbmVkX2ludHJhPTAgYmZyYW1lcz0zIGJfcHlyYW1pZD0yIGJfYWRhcHQ9MSBiX2JpYXM9MCBkaXJlY3Q9MSB3ZWlnaHRiPTEgb3Blbl9nb3A9MCB3ZWlnaHRwPTIga2V5aW50PTI1MCBrZXlpbnRfbWluPTI1IHNjZW5lY3V0PTQwIGludHJhX3JlZnJlc2g9MCByY19sb29rYWhlYWQ9NDAgcmM9Y3JmIG1idHJlZT0xIGNyZj0yMy4wIHFjb21wPTAuNjAgcXBtaW49MCBxcG1heD02OSBxcHN0ZXA9NCBpcF9yYXRpbz0xLjQwIGFxPTE6MS4wMACAAAAApWWIhAAv//72rvzLK0cLlS4dWXuzUfLoSXL9iDB9aAAAAwAAAwAAJuKiZ0WFMeJsgAAALmAIWElDyDzETFWKgSrQcn0YAAJQ==';
        document.body.appendChild(silentVideo);
        const videoPromise = silentVideo.play();
        if (videoPromise !== undefined) {
          await videoPromise.catch(() => {});
        }
        silentVideo.pause();
        silentVideo.remove();
        console.log('🔓 Safari video primer completed');
      } catch (e) {
        console.log('🔓 Safari video primer:', e.name);
      }
    }
    
    // 4. Create additional audio elements for widget priming
    // This helps with SoundCloud and embedded players
    for (let i = 0; i < 3; i++) {
      try {
        const tempAudio = document.createElement('audio');
        tempAudio.src = 'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA';
        tempAudio.volume = 0.001;
        tempAudio.playsInline = true;
        document.body.appendChild(tempAudio);
        const tempPromise = tempAudio.play();
        if (tempPromise !== undefined) {
          await tempPromise.catch(() => {});
        }
        tempAudio.pause();
        tempAudio.remove();
      } catch (e) {
        // Ignore - just priming
      }
    }
    
    isUnlocked = true;
    console.log('🔓 Audio unlocked successfully (attempts:', unlockAttempts, ')');
    return true;
  } catch (error) {
    console.error('🔓 Failed to unlock audio:', error);
    return false;
  }
};

// Check if audio is unlocked
export const isAudioUnlocked = () => {
  return isUnlocked;
};

// Check if Safari browser
export const isSafariBrowser = isSafari;

// Get the shared AudioContext
export const getAudioContext = () => {
  return audioContext;
};

// Force play an audio/video element with retry and Safari-specific handling
export const forcePlay = async (element) => {
  if (!element) return false;
  
  try {
    // First, make sure AudioContext is unlocked
    if (!isUnlocked && audioContext?.state === 'suspended') {
      await audioContext.resume();
    }
    
    // Safari-specific: ensure playsinline is set
    if (isSafari() && element.tagName === 'VIDEO') {
      element.setAttribute('playsinline', '');
      element.setAttribute('webkit-playsinline', '');
    }
    
    // Attempt to play
    const playPromise = element.play();
    
    if (playPromise !== undefined) {
      await playPromise;
      console.log('🎵 Media playback started successfully');
      return true;
    }
    return true;
  } catch (error) {
    console.error('forcePlay failed:', error.name, error.message);
    
    // Safari retry: try muting first then unmuting
    if (isSafari() && error.name === 'NotAllowedError') {
      try {
        element.muted = true;
        await element.play();
        element.muted = false;
        console.log('🎵 Safari fallback: muted play then unmute');
        return true;
      } catch (e2) {
        console.error('Safari fallback also failed:', e2.name);
      }
    }
    
    return false;
  }
};

// Reset unlock state (for testing)
export const resetUnlock = () => {
  isUnlocked = false;
  unlockAttempts = 0;
  if (audioContext) {
    audioContext.close();
    audioContext = null;
  }
};

export default {
  unlockAudio,
  isAudioUnlocked,
  isSafariBrowser,
  getAudioContext,
  forcePlay,
  resetUnlock
};
